/* eslint-disable complexity */
import { useContext } from 'react';
import getConfig from 'next/config';
import { ContextApp } from '@lmig/dotcom-aspect-components/ContextApp';
import urlHelper from '@lmig/dotcom-aspect-helpers/urlHelper';

const {
  publicRuntimeConfig: { environmentalizedDomains },
} = getConfig();

/**
 * Environmentalizes a link depending on the environment and application.
 *
 * @param {Object} options
 * @param {String|undefined} options.link  The link (relative or absolute).
 * @param {String|undefined} options.domain Which application this link should point to.
 * @example
 * import useEnvironmentalizeLink from '../hooks/useEnvironmentalizeLink';
 *
 * const link = useEnvironmentalizeLink({ domain: 'LM_COM', link: '/login' })
 * console.log(link) // https://dev-www2.lbertymutual.com/login
 */
const environmentalizedDomainsKeys = Object.keys(environmentalizedDomains);

const useEnvironmentalizeLink = ({ link = '', domain, bypassContext = false, ssl = false }) => {
  let contextValues;

  if (!bypassContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    contextValues = useContext(ContextApp);

    if (!contextValues) {
      throw new Error('useEnvironmentalizeLink needs to be within a <ContextAppProvider>');
    }
  }

  if (domain !== undefined && !(domain in environmentalizedDomains)) {
    throw new Error(`${domain} is not a valid domain`);
  }

  let activeDomain = environmentalizedDomains[domain];

  if (domain === undefined || domain === environmentalizedDomainsKeys[0]) {
    if (process.env.REGION && process.env.ETE_DOMAIN === environmentalizedDomains[environmentalizedDomainsKeys[0]]) {
      activeDomain = environmentalizedDomains[environmentalizedDomainsKeys[0]];
    } else {
      activeDomain = contextValues?.host || environmentalizedDomains[environmentalizedDomainsKeys[0]];
    }
  }

  if (link.startsWith('/') || activeDomain === '') {
    return `${activeDomain}${link}`;
  }

  // Ensure active domain url is https in non-local envs
  const url = urlHelper(link, activeDomain, ssl);

  return decodeURI(url.href);
};

export default useEnvironmentalizeLink;
